import { useCallback, useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";

import { useAuth } from "../auth/AuthContext";
import { showGlobalToast } from "../components/GlobalToasts";
import { api, ApiError } from "../lib/api";
import { formatDateTime } from "../lib/dateFormat";
import {
  buildDemoMailtoUrl,
  buildDemoWhatsAppUrl,
  DEMO_EMAIL_STATUS_LABELS,
  DEMO_NEXT_TOURNAMENT_OPTIONS,
  DEMO_ORG_TYPE_OPTIONS,
  DEMO_REQUEST_LIMITS,
  DEMO_REQUEST_STATUS_OPTIONS,
  DEMO_SPORT_OPTIONS,
  DEMO_TYPICAL_PLAYERS_OPTIONS,
  demoOptionLabel,
  firstName,
  normalizeDemoNotes,
  type DemoRequestCounts,
  type DemoRequestItem,
  type DemoRequestStatus,
} from "../lib/demoRequest";
import { makeLatestOnly } from "../lib/latestOnly";
import { useMediaQuery } from "../lib/useMediaQuery";

const PAGE_SIZE = 100;

const EMPTY_MESSAGES: Record<DemoRequestStatus, string> = {
  nuevo: "No hay solicitudes nuevas por ahora.",
  contactado: "No hay solicitudes contactadas.",
  demo_agendada: "No hay demos agendadas.",
  cliente: "Aún no hay solicitudes que se hayan vuelto clientes.",
  descartado: "No hay solicitudes descartadas.",
};

function errorText(err: unknown): string {
  return err instanceof ApiError ? err.message : "Error de red. Intenta otra vez.";
}

function sportsText(item: DemoRequestItem): string {
  return item.sports.map((sport) => demoOptionLabel(DEMO_SPORT_OPTIONS, sport)).join(", ") || "—";
}

function orgText(item: DemoRequestItem): string {
  const type = item.org_type ? demoOptionLabel(DEMO_ORG_TYPE_OPTIONS, item.org_type) : null;
  if (item.org_name && type) return `${item.org_name} · ${type}`;
  return item.org_name || type || "—";
}

function originText(item: DemoRequestItem): string {
  const utm = [item.utm_source, item.utm_medium, item.utm_campaign].filter(Boolean).join(" / ");
  const parts = [utm ? `UTM: ${utm}` : null, item.referrer_host ? `Desde: ${item.referrer_host}` : null];
  const text = parts.filter(Boolean).join(" · ");
  return text || "Directo";
}

/** Tope por petición del servidor (LIST_MAX_LIMIT en leads/services.py). */
const RELOAD_MAX_ROWS = 1000;

type PendingFocus =
  | { kind: "select"; id: number }
  | { kind: "tab-if-row-left"; id: number }
  | null;

/** Bandeja del superadministrador para las solicitudes de "Pedir demo". */
export default function SuperadminDemoRequestsPage() {
  const { token } = useAuth();
  const isWide = useMediaQuery("(min-width: 960px)", true);
  const [statusFilter, setStatusFilter] = useState<DemoRequestStatus>("nuevo");
  // Pestaña VIGENTE para las respuestas que llegan tarde (un cierre viejo
  // tendría la pestaña de cuando se lanzó la petición).
  const statusFilterRef = useRef<DemoRequestStatus>("nuevo");
  const [items, setItems] = useState<DemoRequestItem[]>([]);
  const itemsRef = useRef<DemoRequestItem[]>([]);
  const [counts, setCounts] = useState<DemoRequestCounts | null>(null);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [actionError, setActionError] = useState<string | null>(null);
  // Región viva PERMANENTE: su texto cambia después de cada acción.
  const [liveText, setLiveText] = useState("");
  // Un guardado de estado a la vez POR FILA (las demás filas siguen usables).
  const [savingStatusIds, setSavingStatusIds] = useState<ReadonlySet<number>>(() => new Set());
  const savingStatusRef = useRef<Set<number>>(new Set());
  // Estado elegido en la lista y aún sin guardar (elegir ≠ guardar: mover
  // la lista con las flechas del teclado no debe cambiar nada).
  const [statusChoices, setStatusChoices] = useState<Record<number, DemoRequestStatus>>({});
  // Borradores de notas: estado para pintar + ref para leer el valor vigente
  // cuando responde el servidor. Un solo guardado de notas en vuelo por fila.
  const [noteDrafts, setNoteDrafts] = useState<Record<number, string>>({});
  const draftsRef = useRef<Record<number, string>>({});
  const notesInFlight = useRef<Set<number>>(new Set());
  const [savedNotesId, setSavedNotesId] = useState<number | null>(null);
  const selectRefs = useRef<Map<number, HTMLSelectElement>>(new Map());
  const notesRefs = useRef<Map<number, HTMLTextAreaElement>>(new Map());
  const tabRefs = useRef<Map<DemoRequestStatus, HTMLButtonElement>>(new Map());
  const pendingFocus = useRef<PendingFocus>(null);
  // Recargas posteriores a un guardado en vuelo: mientras haya alguna,
  // "Ver más" se deshabilita (si no, su respuesta desplazaría a la recarga).
  const [reloadsInFlight, setReloadsInFlight] = useState(0);
  const latest = useRef(makeLatestOnly());

  useEffect(() => {
    itemsRef.current = items;
  }, [items]);

  // El foco se mueve DESPUÉS de pintar (un control deshabilitado mientras
  // guarda no puede recibirlo antes). Siempre sin desplazar la página, y
  // SOLO si el foco se perdió o sigue dentro de esa misma fila: nunca se le
  // quita a quien está escribiendo en otra fila.
  useEffect(() => {
    const request = pendingFocus.current;
    if (!request) return;
    pendingFocus.current = null;
    const active = document.activeElement;
    const focusIsFree =
      !active ||
      active === document.body ||
      !active.isConnected ||
      Boolean(active.closest(`[data-demo-row="${request.id}"]`));
    if (!focusIsFree) return;
    if (request.kind === "select") {
      selectRefs.current.get(request.id)?.focus({ preventScroll: true });
      return;
    }
    tabRefs.current.get(statusFilterRef.current)?.focus({ preventScroll: true });
  });

  function notesHasFocus(id: number): boolean {
    const textarea = notesRefs.current.get(id);
    return Boolean(textarea) && document.activeElement === textarea;
  }

  const load = useCallback(
    async (
      status: DemoRequestStatus,
      options: { quiet?: boolean; limit?: number } = {},
    ): Promise<DemoRequestItem[] | null> => {
      if (!token) return null;
      const attempt = latest.current.begin();
      if (!options.quiet) setLoading(true);
      setLoadError(null);
      try {
        const response = await api.superadmin.demoRequests.list(token, {
          status,
          limit: options.limit ?? PAGE_SIZE,
          offset: 0,
        });
        if (!latest.current.isCurrent(attempt)) return null;
        setItems(response.data.items);
        setCounts(response.data.counts);
        setTotal(response.data.total);
        return response.data.items;
      } catch (err) {
        if (latest.current.isCurrent(attempt)) setLoadError(errorText(err));
        return null;
      } finally {
        if (latest.current.isCurrent(attempt)) setLoading(false);
      }
    },
    [token],
  );

  useEffect(() => {
    void load(statusFilter);
  }, [load, statusFilter]);

  function setDraft(id: number, value: string | undefined) {
    const next = { ...draftsRef.current };
    if (value === undefined) delete next[id];
    else next[id] = value;
    draftsRef.current = next;
    setNoteDrafts(next);
  }

  function setChoice(id: number, value: DemoRequestStatus | undefined) {
    setStatusChoices((current) => {
      const next = { ...current };
      if (value === undefined) delete next[id];
      else next[id] = value;
      return next;
    });
  }

  function markSavingStatus(id: number, saving: boolean) {
    if (saving) savingStatusRef.current.add(id);
    else savingStatusRef.current.delete(id);
    setSavingStatusIds(new Set(savingStatusRef.current));
  }

  /** Error de una acción del usuario: aviso flotante UNA vez + texto fijo
   * (el texto lleva data-no-toast para no volver a saltar con cada cambio). */
  function reportError(err: unknown) {
    const text = errorText(err);
    setActionError(text);
    setLiveText(text);
    showGlobalToast(text, "error");
  }

  function changeTab(next: DemoRequestStatus) {
    if (next === statusFilter) return;
    statusFilterRef.current = next;
    setActionError(null);
    setSavedNotesId(null);
    setItems([]);
    setStatusFilter(next);
  }

  async function loadMore() {
    if (!token) return;
    const status = statusFilterRef.current;
    const attempt = latest.current.begin();
    setLoadingMore(true);
    try {
      const response = await api.superadmin.demoRequests.list(token, {
        status,
        limit: PAGE_SIZE,
        offset: itemsRef.current.length,
      });
      if (!latest.current.isCurrent(attempt)) return;
      setItems((current) => [...current, ...response.data.items]);
      setCounts(response.data.counts);
      setTotal(response.data.total);
    } catch (err) {
      if (latest.current.isCurrent(attempt)) reportError(err);
    } finally {
      setLoadingMore(false);
    }
  }

  async function saveStatus(item: DemoRequestItem) {
    const nextStatus = statusChoices[item.id];
    if (!token || !nextStatus || nextStatus === item.status) return;
    if (savingStatusRef.current.has(item.id)) return;
    markSavingStatus(item.id, true);
    setActionError(null);
    let failed = false;
    try {
      const response = await api.superadmin.demoRequests.update(token, item.id, { status: nextStatus });
      const label = demoOptionLabel(DEMO_REQUEST_STATUS_OPTIONS, response.data.item.status);
      const done = `La solicitud de ${firstName(item.full_name) || item.full_name} quedó como «${label}».`;
      setLiveText(done);
      showGlobalToast(done, "success");
      setChoice(item.id, undefined);
      // Recarga del servidor con la pestaña VIGENTE y todo lo que ya se veía
      // (incluidas las filas de "Ver más"): contadores y total salen exactos.
      setReloadsInFlight((count) => count + 1);
      let rows: DemoRequestItem[] | null;
      try {
        rows = await load(statusFilterRef.current, {
          quiet: true,
          limit: Math.min(RELOAD_MAX_ROWS, Math.max(PAGE_SIZE, itemsRef.current.length)),
        });
      } finally {
        setReloadsInFlight((count) => Math.max(0, count - 1));
      }
      if (rows && !rows.some((row) => row.id === item.id)) {
        pendingFocus.current = { kind: "tab-if-row-left", id: item.id };
      }
    } catch (err) {
      failed = true;
      reportError(err);
    } finally {
      markSavingStatus(item.id, false);
      if (failed) pendingFocus.current = { kind: "select", id: item.id };
    }
  }

  function cancelStatus(item: DemoRequestItem) {
    setChoice(item.id, undefined);
    selectRefs.current.get(item.id)?.focus({ preventScroll: true });
  }

  /** El borrador se suelta SOLO si el campo no tiene el foco (nunca se toca
   * lo que alguien está escribiendo) y si es IDÉNTICO, sin normalizar, a lo
   * guardado (un espacio o salto de línea al final no se pierde). */
  function releaseDraftIfSaved(id: number, serverNotes: string | null, leaving: boolean) {
    const draft = draftsRef.current[id];
    if (draft === undefined || (!leaving && notesHasFocus(id))) return;
    if (draft === (serverNotes ?? "")) setDraft(id, undefined);
  }

  /** Guarda las notas de UNA fila, una petición a la vez. Al responder, si el
   * borrador vigente (normalizado) es distinto de lo que YA tiene el
   * servidor, se manda de nuevo (hasta que coincidan). */
  async function flushNotes(id: number, options: { leaving?: boolean } = {}) {
    if (!token || notesInFlight.current.has(id)) return;
    // Al salir del campo (onBlur) el foco todavía puede reportarse en el
    // textarea; después de la primera respuesta se consulta el foco real.
    let leaving = Boolean(options.leaving);
    const row = itemsRef.current.find((candidate) => candidate.id === id);
    if (!row) return;
    notesInFlight.current.add(id);
    let serverNotes: string | null = row.notes;
    let lastSent: string | null | undefined;
    let saved = false;
    setActionError(null);
    setSavedNotesId(null);
    try {
      for (;;) {
        const draft = draftsRef.current[id];
        if (draft === undefined) break;
        const wanted = normalizeDemoNotes(draft);
        if (wanted === serverNotes) {
          releaseDraftIfSaved(id, serverNotes, leaving);
          break;
        }
        // El servidor guardó otra forma del mismo texto: no se insiste.
        if (lastSent !== undefined && wanted === lastSent) break;
        lastSent = wanted;
        const response = await api.superadmin.demoRequests.update(token, id, { notes: wanted });
        leaving = false;
        const updated = response.data.item;
        serverNotes = updated.notes;
        saved = true;
        setItems((current) =>
          current.map((candidate) =>
            candidate.id === id ? { ...candidate, notes: updated.notes, updated_at: updated.updated_at } : candidate,
          ),
        );
      }
      const pending = draftsRef.current[id];
      if (saved && (pending === undefined || normalizeDemoNotes(pending) === serverNotes)) {
        setSavedNotesId(id);
        setLiveText(`Notas de ${firstName(row.full_name) || row.full_name} guardadas.`);
      }
    } catch (err) {
      reportError(err);
    } finally {
      notesInFlight.current.delete(id);
    }
  }

  function renderActions(item: DemoRequestItem) {
    const busy = savingStatusIds.has(item.id);
    const chosen = statusChoices[item.id] ?? item.status;
    const pendingStatus = chosen !== item.status;
    const notesValue = noteDrafts[item.id] ?? item.notes ?? "";
    return (
      <div className="demo-requests__actions">
        <div className="demo-requests__links">
          <a
            className="btn btn--compact"
            href={buildDemoWhatsAppUrl(item)}
            target="_blank"
            rel="noopener noreferrer"
          >
            WhatsApp
          </a>
          <a className="btn btn--compact btn--ghost" href={buildDemoMailtoUrl(item.email)}>
            Correo
          </a>
        </div>
        <label className="demo-requests__field">
          <span>Estado</span>
          <select
            ref={(element) => {
              if (element) selectRefs.current.set(item.id, element);
              else selectRefs.current.delete(item.id);
            }}
            value={chosen}
            disabled={busy}
            onChange={(event) => {
              const value = event.target.value as DemoRequestStatus;
              setChoice(item.id, value === item.status ? undefined : value);
            }}
          >
            {DEMO_REQUEST_STATUS_OPTIONS.map((option) => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </select>
        </label>
        {pendingStatus ? (
          <div className="demo-requests__links">
            <button
              type="button"
              className="btn btn--compact"
              disabled={busy}
              onClick={() => void saveStatus(item)}
            >
              {busy ? "Guardando…" : "Guardar estado"}
            </button>
            <button
              type="button"
              className="btn btn--compact btn--ghost"
              disabled={busy}
              onClick={() => cancelStatus(item)}
            >
              Cancelar
            </button>
          </div>
        ) : null}
        <label className="demo-requests__field">
          <span>Notas</span>
          <textarea
            ref={(element) => {
              if (element) notesRefs.current.set(item.id, element);
              else notesRefs.current.delete(item.id);
            }}
            rows={3}
            maxLength={DEMO_REQUEST_LIMITS.notesMax}
            value={notesValue}
            placeholder="Se guardan al salir del campo"
            onChange={(event) => {
              setSavedNotesId(null);
              setDraft(item.id, event.target.value);
            }}
            onBlur={() => void flushNotes(item.id, { leaving: true })}
          />
        </label>
        {savedNotesId === item.id ? (
          <p className="muted demo-requests__saved" data-no-toast>
            Notas guardadas.
          </p>
        ) : null}
        {item.contacted_at ? (
          <p className="muted demo-requests__saved">Primer contacto: {formatDateTime(item.contacted_at)}</p>
        ) : null}
      </div>
    );
  }

  function emailPill(item: DemoRequestItem) {
    const warn = item.email_status === "failed" || item.email_status === "skipped";
    return (
      <span className={`status-pill${warn ? " status-pill--warn" : ""}`}>
        {DEMO_EMAIL_STATUS_LABELS[item.email_status] ?? item.email_status}
      </span>
    );
  }

  const activeTab = DEMO_REQUEST_STATUS_OPTIONS.find((option) => option.value === statusFilter);

  return (
    <>
      <section className="card">
        <div className="card__header">
          <div>
            <h1>Solicitudes de demo</h1>
            <p className="muted">
              Organizadores que pidieron una demo desde torneos123.com/organizador.
            </p>
          </div>
          <div className="table-actions" role="group" aria-label="Filtrar por estado">
            {DEMO_REQUEST_STATUS_OPTIONS.map((option) => (
              <button
                key={option.value}
                ref={(element) => {
                  if (element) tabRefs.current.set(option.value, element);
                  else tabRefs.current.delete(option.value);
                }}
                type="button"
                className={`btn btn--compact${statusFilter === option.value ? "" : " btn--ghost"}`}
                aria-pressed={statusFilter === option.value}
                onClick={() => changeTab(option.value)}
              >
                {option.tab}
                {counts ? ` (${counts[option.value]})` : ""}
              </button>
            ))}
          </div>
        </div>

        <div className="alert alert--info demo-requests__help" data-no-toast>
          <strong>Al convertir a cliente:</strong> crea su cuenta de organizador y,{" "}
          <strong>antes de que abra inscripciones</strong>, pon en $0 la comisión de su primer torneo en
          Superadmin → <Link to="/superadmin/fees">Comisiones</Link> (la comisión se congela en cada orden).
        </div>

        {loadError ? (
          <p className="form-error" role="alert" data-no-toast>
            {loadError}
          </p>
        ) : null}
        {actionError ? (
          <p className="form-error" role="alert" data-no-toast>
            {actionError}
          </p>
        ) : null}
        {/* Región viva permanente: los lectores de pantalla anuncian cada
            resultado sin mover el foco ni la página. */}
        <p className="muted demo-requests__live" role="status" aria-live="polite" data-no-toast>
          {liveText}
        </p>

        {loading ? (
          <p className="muted">Cargando solicitudes…</p>
        ) : loadError ? null : items.length === 0 ? (
          <p className="muted">{EMPTY_MESSAGES[statusFilter]}</p>
        ) : isWide ? (
          <div className="responsive-table-wrap demo-requests__table-wrap">
            <table className="data-table demo-requests__table">
              <caption className="sr-only">Solicitudes de demo: {activeTab?.tab ?? ""}</caption>
              <thead>
                <tr>
                  <th scope="col">Fecha</th>
                  <th scope="col">Contacto</th>
                  <th scope="col">Organización</th>
                  <th scope="col">Ciudad</th>
                  <th scope="col">Deportes</th>
                  <th scope="col">Jugadores</th>
                  <th scope="col">Próximo torneo</th>
                  <th scope="col">Mensaje</th>
                  <th scope="col">Origen</th>
                  <th scope="col">Aviso</th>
                  <th scope="col">Seguimiento</th>
                </tr>
              </thead>
              <tbody>
                {items.map((item) => (
                  <tr key={item.id} data-demo-row={item.id}>
                    <td>{formatDateTime(item.created_at)}</td>
                    <td>
                      <strong>{item.full_name}</strong>
                      <br />
                      <span className="muted">{item.phone}</span>
                      <br />
                      <span className="muted">{item.email}</span>
                    </td>
                    <td>{orgText(item)}</td>
                    <td>{item.city}</td>
                    <td>{sportsText(item)}</td>
                    <td>{demoOptionLabel(DEMO_TYPICAL_PLAYERS_OPTIONS, item.typical_players)}</td>
                    <td>{demoOptionLabel(DEMO_NEXT_TOURNAMENT_OPTIONS, item.next_tournament)}</td>
                    <td className="demo-requests__message">{item.message || "—"}</td>
                    <td>{originText(item)}</td>
                    <td>{emailPill(item)}</td>
                    <td>{renderActions(item)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <div className="demo-requests__cards">
            {items.map((item) => (
              <article key={item.id} className="demo-requests__card" data-demo-row={item.id}>
                <header>
                  <strong>{item.full_name}</strong>
                  {emailPill(item)}
                </header>
                <p className="muted">
                  {formatDateTime(item.created_at)} · {item.city}
                </p>
                <dl className="demo-requests__facts">
                  <div>
                    <dt>Celular</dt>
                    <dd>{item.phone}</dd>
                  </div>
                  <div>
                    <dt>Correo</dt>
                    <dd>{item.email}</dd>
                  </div>
                  <div>
                    <dt>Organización</dt>
                    <dd>{orgText(item)}</dd>
                  </div>
                  <div>
                    <dt>Deportes</dt>
                    <dd>{sportsText(item)}</dd>
                  </div>
                  <div>
                    <dt>Jugadores</dt>
                    <dd>{demoOptionLabel(DEMO_TYPICAL_PLAYERS_OPTIONS, item.typical_players)}</dd>
                  </div>
                  <div>
                    <dt>Próximo torneo</dt>
                    <dd>{demoOptionLabel(DEMO_NEXT_TOURNAMENT_OPTIONS, item.next_tournament)}</dd>
                  </div>
                  <div>
                    <dt>Origen</dt>
                    <dd>{originText(item)}</dd>
                  </div>
                </dl>
                {item.message ? <p className="demo-requests__message">{item.message}</p> : null}
                {renderActions(item)}
              </article>
            ))}
          </div>
        )}

        {!loading && !loadError && items.length < total ? (
          <div className="demo-requests__more">
            <button
              type="button"
              className="btn btn--ghost"
              disabled={loadingMore || reloadsInFlight > 0}
              onClick={() => void loadMore()}
            >
              {loadingMore ? "Cargando…" : `Ver más (${total - items.length})`}
            </button>
          </div>
        ) : null}
      </section>
    </>
  );
}
